<template>
	<div class="g-playback">

		<div class="g-playback__grid">

			<!-- Left: form -->
			<div class="g-playback__form">
				<div>
					<label class="g-playback__label">{{ $t('guaranteed.content_items_per_show') }} *</label>
					<input
						type="number"
						min="1"
						max="20"
						step="1"
						class="g-playback__input g-playback__input--num"
						:value="contentShowCount"
						@input="updateContentShowCount($event.target.value)"
					/>
					<p class="g-playback__help">{{ $t('guaranteed.content_items_per_show_hint') }}</p>
				</div>
				<div>
					<label class="g-playback__label">{{ $t('guaranteed.playback_order') }} *</label>
					<select
						class="g-playback__select"
						:value="playbackOrder"
						@change="updatePlaybackOrder($event.target.value)"
					>
						<option :value="2">{{ $t('guaranteed.playback_order_sequential') }}</option>
						<option :value="1">{{ $t('guaranteed.playback_order_random') }}</option>
					</select>
					<p class="g-playback__help">{{ $t('guaranteed.playback_order_hint') }}</p>
				</div>
			</div>

			<!-- Right: explainer -->
			<div class="g-playback__explainer">
				<div class="g-playback__ex-title">{{ $t('guaranteed.playback_explainer_title') }}</div>
				<p v-html="$t('guaranteed.playback_explainer_intro')" />
				<div>
					<div class="g-playback__help g-playback__help--inset">{{ $t('guaranteed.playback_explainer_cycle1') }}</div>
					<div class="g-playback__chips">
						<span class="g-playback__chip">1</span>
						<span class="g-playback__chip">2</span>
						<span class="g-playback__chip g-playback__chip--muted">3</span>
					</div>
				</div>
				<div>
					<div class="g-playback__help g-playback__help--inset">{{ $t('guaranteed.playback_explainer_cycle2') }}</div>
					<div class="g-playback__chips">
						<span class="g-playback__chip g-playback__chip--muted">1</span>
						<span class="g-playback__chip g-playback__chip--muted">2</span>
						<span class="g-playback__chip">3</span>
						<span class="g-playback__chip">1</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Book without content -->
		<label
			class="g-playback__check"
			:class="{ 'g-playback__check--on': bookWithoutContent }"
		>
			<input
				type="checkbox"
				class="g-playback__check-native"
				:checked="bookWithoutContent"
				@change="updateBookWithoutContent($event.target.checked)"
			/>
			<span class="g-playback__check-box">
				<svg
					v-if="bookWithoutContent"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="3"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="20 6 9 17 4 12" />
				</svg>
			</span>
			<span class="g-playback__check-body">
				<span class="g-playback__check-title">{{ $t('guaranteed.book_without_content') }}</span>
				<span class="g-playback__check-sub">{{ $t('guaranteed.book_without_content_hint') }}</span>
			</span>
		</label>

	</div>
</template>


<script>
const MODEL = Object.freeze({ prop: 'value', event: 'input' });

const DEFAULT_RULES = Object.freeze({
	content_show_count: 1,
	playback_order: 2, // 2 = Sequential, 1 = Random
});

export default {
	name: 'GuaranteedPlaybackRules',

	model: MODEL,

	props: {
		[MODEL.prop]: {
			type: Object,
			default: () => ({ ...DEFAULT_RULES }),
		},
		bookWithoutContent: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		contentShowCount() {
			const v = this[MODEL.prop] && this[MODEL.prop].content_show_count;
			return v == null ? DEFAULT_RULES.content_show_count : v;
		},
		playbackOrder() {
			const v = this[MODEL.prop] && this[MODEL.prop].playback_order;
			return v == null ? DEFAULT_RULES.playback_order : v;
		},
	},

	methods: {
		emitRules(next) {
			this.$emit(MODEL.event, next);
		},
		updateContentShowCount(value) {
			const n = Math.max(1, parseInt(value, 10) || 1);
			this.emitRules({ ...this[MODEL.prop], content_show_count: n });
		},
		updatePlaybackOrder(value) {
			const n = parseInt(value, 10) === 1 ? 1 : 2;
			this.emitRules({ ...this[MODEL.prop], playback_order: n });
		},
		updateBookWithoutContent(checked) {
			this.$emit('update:bookWithoutContent', !!checked);
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$purple: $v2-purple
$border: $v2-border
$card: $v2-card-bg
$text: $v2-text
$muted: $v2-text-muted
$bg: #f3f4f6
$font: $v2-font
$r-input: 10px

.g-playback
	font-family: $font

.g-playback__grid
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 18px
	align-items: stretch

	@media (max-width: 768px)
		grid-template-columns: 1fr

.g-playback__form
	display: flex
	flex-direction: column
	gap: 16px

.g-playback__label
	display: block
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $muted
	margin-bottom: 7px

.g-playback__input
	width: 100%
	height: 38px
	padding: 8px 12px
	border: 1px solid $border
	border-radius: $r-input
	font-size: 14px
	font-family: 'JetBrains Mono', monospace
	background: rgba($bg, 0.55)
	color: $text
	transition: all .15s

	&:focus
		outline: none
		border-color: $purple
		background: #fff
		box-shadow: 0 0 0 3px rgba($purple, 0.14)

.g-playback__input--num
	max-width: 180px

.g-playback__select
	width: 100%
	height: 38px
	padding: 0 32px 0 12px
	border: 1px solid $border
	border-radius: $r-input
	font-size: 14px
	font-family: $font
	background: rgba($bg, 0.55) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center
	color: $text
	max-width: 280px
	appearance: none
	transition: all .15s

	&:focus
		outline: none
		border-color: $purple
		background-color: #fff
		box-shadow: 0 0 0 3px rgba($purple, 0.14)

.g-playback__help
	font-size: 11.5px
	color: $muted
	margin-top: 6px
	line-height: 1.5

.g-playback__help--inset
	margin: 6px 0

// ── Explainer ──────────────────────
.g-playback__explainer
	background: rgba($purple, 0.04)
	border: 1px dashed rgba($purple, 0.35)
	border-radius: 12px
	padding: 16px 18px
	font-size: 12.5px
	color: $text
	line-height: 1.55
	display: flex
	flex-direction: column
	gap: 8px

	::v-deep b
		color: $purple
		font-weight: 700

.g-playback__ex-title
	font-size: 11px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $purple

.g-playback__chips
	display: flex
	gap: 6px
	flex-wrap: wrap

.g-playback__chip
	display: inline-flex
	align-items: center
	justify-content: center
	min-width: 30px
	height: 30px
	padding: 0 10px
	border-radius: 7px
	font-size: 12px
	font-weight: 700
	background: $purple
	color: #fff
	border: 1px solid $purple
	font-family: 'JetBrains Mono', monospace
	box-shadow: 0 1px 3px rgba($purple, 0.25)

.g-playback__chip--muted
	background: rgba($bg, 0.7)
	color: $muted
	border-color: $border
	box-shadow: none

// ── Book without content ───────────
.g-playback__check
	display: flex
	align-items: flex-start
	gap: 14px
	padding: 16px 18px
	border: 1.5px solid $border
	border-radius: 12px
	background: $card
	cursor: pointer
	transition: all .15s
	margin-top: 18px

	&:hover
		border-color: rgba($purple, 0.4)

.g-playback__check--on
	border-color: $purple
	background: linear-gradient(135deg, rgba($purple, 0.06), transparent 60%)

.g-playback__check-native
	position: absolute
	opacity: 0
	pointer-events: none
	width: 0
	height: 0

.g-playback__check-box
	width: 22px
	height: 22px
	border-radius: 6px
	border: 1.5px solid $border
	display: inline-flex
	align-items: center
	justify-content: center
	background: #fff
	flex-shrink: 0
	margin-top: 1px
	transition: all .15s

	svg
		width: 13px
		height: 13px
		color: #fff

	.g-playback__check--on &
		background: $purple
		border-color: $purple

.g-playback__check-body
	display: flex
	flex-direction: column
	gap: 3px

.g-playback__check-title
	font-size: 13.5px
	font-weight: 700
	color: $text

.g-playback__check-sub
	font-size: 12.5px
	color: $muted
	line-height: 1.5
</style>
